import { useState } from 'react';
import {
  NO_PLAN_REASONS_MAX,
  NO_PLAN_REASON_MAX,
  cleanNoPlanReason,
  type PracticeNoteSettings,
} from '@ehr/shared';

/**
 * ehr#708 (DISCHARGE_SUMMARY_NOTE_10-30-V6, Jay's answers 10-30 Q2 and Q3): on a discharge summary,
 * Note settings > Practice note settings also has "Goal status required at sign-off" (on unless an
 * admin or supervisor turns it off) and the "No treatment plan on file" reasons the note offers
 * (the practice adds to the four it starts with). Saved with the window's Save, for the practice.
 */
export function DischargePracticeSettings({
  value,
  onChange,
}: {
  value: PracticeNoteSettings;
  onChange: (v: PracticeNoteSettings) => void;
}) {
  const [text, setText] = useState('');
  const [err, setErr] = useState('');
  const reasons = value.dischargeNoPlanReasons;
  const setReasons = (list: string[]) => onChange({ ...value, dischargeNoPlanReasons: list });
  const add = () => {
    const t = cleanNoPlanReason(text);
    if (!t) return;
    if (reasons.some((r) => r.toLowerCase() === t.toLowerCase()))
      return setErr(`${t} is already on the list.`);
    if (reasons.length >= NO_PLAN_REASONS_MAX)
      return setErr(`The list is full (${NO_PLAN_REASONS_MAX}).`);
    setReasons([...reasons, t]);
    setText('');
    setErr('');
  };
  return (
    <div className="pn-ns-discharge" data-test="ns-discharge">
      <div className="pn-ns-sws">
        <label className="pn-ns-sw">
          <input
            type="checkbox"
            checked={value.requireGoalStatus}
            onChange={(e) => onChange({ ...value, requireGoalStatus: e.target.checked })}
          />
          Goal status required at sign-off
        </label>
      </div>
      <div className="pn-ns-keep" data-test="ns-noplan">
        <span className="pn-ns-keep-h">No treatment plan on file reasons</span>
        <ul className="pn-inter-list">
          {reasons.map((r) => (
            <li key={r} className="pn-inter-row">
              <span className="pn-inter-name">{r}</span>
              <button
                type="button"
                className="pn-link"
                aria-label={`Remove ${r}`}
                disabled={reasons.length <= 1}
                onClick={() => setReasons(reasons.filter((x) => x !== r))}
              >
                Remove
              </button>
            </li>
          ))}
        </ul>
        <div className="pn-inter-add">
          <input
            className="input"
            value={text}
            maxLength={NO_PLAN_REASON_MAX}
            placeholder="Add a reason"
            aria-label="Add a reason"
            onChange={(e) => {
              setText(e.target.value);
              setErr('');
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                add();
              }
            }}
          />
          <button type="button" className="btn small" onClick={add} disabled={!text.trim()}>
            Add
          </button>
        </div>
        {err && (
          <p className="pn-inline-err" role="alert">
            {err}
          </p>
        )}
      </div>
    </div>
  );
}
